type Cleanup = () => void;

const INTERACTION_EVENTS: (keyof WindowEventMap)[] = [
  'pointerdown',
  'mousedown',
  'mousemove',
  'keydown',
  'touchstart',
  'touchmove',
  'scroll',
  'wheel',
  'click',
];

/**
 * Runs `callback` once on the first user interaction, or after `fallbackMs`
 * for fully passive sessions.
 *
 * Real visitors trigger the callback on their first scroll, mouse move or
 * touch, which happens within the first seconds of a session; the fallback
 * guarantees fully passive sessions still load everything. Note for tuning:
 * Lighthouse/PSI lab runs never interact, so the fallback timer decides
 * whether a script shows up in the lab trace (timers under ~8s land inside
 * it; callers currently choose short timers on purpose to guarantee data
 * capture for passive visitors).
 *
 * A page that is already scrolled at attach time also counts as interacted:
 * the listeners only attach after hydration, so a visitor who scrolls during
 * hydration (or whose scroll position the browser restored on reload) has
 * already produced their interaction where no listener could see it, and may
 * go fully idle afterwards. Lab runs never scroll, so this path never fires
 * in a trace.
 */
export const onFirstInteraction = (callback: () => void, fallbackMs?: number): Cleanup => {
  if (typeof window === 'undefined') {
    return () => {};
  }

  let fired = false;
  let fallbackTimer: number | null = null;
  let detach: Cleanup = () => {};

  const handler = () => {
    if (fired) {
      return;
    }
    fired = true;
    detach();
    // Hop off the input frame before running the callback: it kicks off
    // script injection work, and doing that inside the arming interaction
    // would count against that interaction's INP.
    window.setTimeout(callback, 0);
  };

  detach = () => {
    INTERACTION_EVENTS.forEach(event => {
      window.removeEventListener(event, handler);
    });
    if (fallbackTimer !== null) {
      window.clearTimeout(fallbackTimer);
      fallbackTimer = null;
    }
  };

  INTERACTION_EVENTS.forEach(event => {
    window.addEventListener(event, handler, { once: true, passive: true });
  });

  if (typeof fallbackMs === 'number') {
    fallbackTimer = window.setTimeout(handler, fallbackMs);
  }

  // Already-scrolled page: the interaction happened before we could listen
  // (scroll during hydration, restored scroll position), so fire now. This
  // is what makes bottom-of-page forms load for visitors who scroll down
  // and then wait without touching anything else.
  if (window.scrollY > 0) {
    handler();
  }

  return detach;
};

export default onFirstInteraction;
